import React, { useEffect, useState } from "react";
import { Form, OverlayTrigger, Tooltip, Button, Modal } from "react-bootstrap";
import { IconButton, Typography, Box } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonIcon from "@mui/icons-material/Person";
import RealEstateAgentIcon from '@mui/icons-material/RealEstateAgent';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getAspectRatio } from "../utils/PreviewUtils";

// Small preview under an image field, with a "View" button that opens the
// full image. The object URL is created once per file and released on cleanup
// (previously a new one was created on every render and never released).
const ImageThumb = ({ value, label, onView }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (value instanceof Blob) {
      const url = URL.createObjectURL(value);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    }
    setSrc(typeof value === "string" ? value : null);
  }, [value]);

  if (!src) return null;

  return (
    <Box sx={{ mt: 1, position: "relative" }}>
      <Box
        component="img"
        src={src}
        alt="thumb"
        onClick={() => onView(src, label)}
        sx={{
          width: "100%",
          height: 44,
          borderRadius: '6px',
          objectFit: 'contain',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          cursor: "zoom-in",
          display: "block",
        }}
      />
      <Button
        type="button"
        variant="light"
        size="sm"
        onClick={() => onView(src, label)}
        aria-label={`View ${label} larger`}
        style={{
          position: "absolute",
          top: "50%",
          right: 4,
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          fontSize: "0.75rem",
          fontWeight: 600,
          color: "#2f539b",
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
      >
        <VisibilityIcon sx={{ fontSize: 14 }} />
      </Button>
    </Box>
  );
};

const ReusableForm = ({ selectedTemplateConfig, formFields, onChange, onFocus, handleGeneratePreview, highlightFieldName, fieldErrors = {} }) => {
  const groupLabels = {
    contact: "VARIABLE Fields",
    customer: "FIXED Fields",
  };
  const [validated, setValidated] = useState(false);
  // Image shown in the large preview: { src, label } or null when closed
  const [viewImage, setViewImage] = useState(null);
  const sections = {
    contact: selectedTemplateConfig.contact || [],
    customer: selectedTemplateConfig.customer || [],
  };

  return (
    <Form
      noValidate
      className="premium-form"
      onSubmit={(e) => {
        e.preventDefault();
        setValidated(true);
        const hasErrors = Object.entries(selectedTemplateConfig).some(
          ([group, fields]) =>
            Array.isArray(fields) &&
            fields.some(
              (f) =>
                (f.required === true || f.required === "true") &&
                (!formFields[group]?.[f.name] ||
                  (f.type !== "file" && !formFields[group]?.[f.name]?.toString()?.trim()) ||
                  (f.type === "file" && formFields[group]?.[f.name] === f.defaultValue))
            )
        );
        if (hasErrors) {
          const firstInvalid = document.querySelector(".is-invalid");
          if (firstInvalid) {
            firstInvalid.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstInvalid.focus();
          }
          return;
        }
        handleGeneratePreview();
      }}
    >
      {Object.entries(sections).map(([group, fields]) => (
        <fieldset key={group} style={{ marginBottom: '10px', border: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            {group === 'contact' ? <PersonIcon sx={{ color: '#2f539b' }} /> : <RealEstateAgentIcon sx={{ color: '#2f539b' }} />}
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
              {groupLabels[group]}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            {fields.map((field) => (
              <Form.Group
                controlId={`form-${field.name}`}
                key={field.name}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Form.Label style={{ fontWeight: 600, color: '#475569', fontSize: '0.85rem', margin: 0 }}>
                    {field.label}
                    {(field.required === "true" || field.required === true) && (
                      <span style={{ color: "#ef4444", marginLeft: '4px' }}>*</span>
                    )}
                  </Form.Label>
                  {field.maxLength && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{`Max ${field.maxLength} characters`}</Tooltip>}
                    >
                      <IconButton size="small" tabIndex={-1} sx={{ color: '#94a3b8', p: 0.5 }}>
                        <HelpOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </OverlayTrigger>
                  )}
                </Box>

                {field.type === "file" ? (
                  <Box sx={{ mb: 0.5 }}>
                    <Form.Control
                      type="file"
                      name={field.name}
                      onChange={(e) => onChange(e, group, field.name, field.type, field.imageSize)}
                      onFocus={() => onFocus(field.variableName || field.name, selectedTemplateConfig.type)}
                      isInvalid={
                        (validated &&
                          (field.required === true || field.required === "true") &&
                          (!formFields[group]?.[field.name] || formFields[group]?.[field.name] === field.defaultValue)) ||
                        !!fieldErrors[`${group}-${field.name}`]
                      }
                      accept="image/*"
                      style={{
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        backgroundColor: (field.variableName || field.name) === highlightFieldName ? '#fffbeb' : '#fff',
                        border: fieldErrors[`${group}-${field.name}`] ? '2px solid #ef4444' : ((field.variableName || field.name) === highlightFieldName ? '2px solid #f59e0b' : '1px solid #e2e8f0'),
                        boxShadow: fieldErrors[`${group}-${field.name}`] ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : ((field.variableName || field.name) === highlightFieldName ? '0 0 0 3px rgba(245, 158, 11, 0.1)' : 'none'),
                      }}
                    />
                    {field.imageSize && (
                      <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem", mt: 0.5, display: "block" }}>
                        <span style={{ fontWeight: 600 }}>Size-</span>
                        {field.imageSize}px,
                        <span style={{ fontWeight: 600 }}> Ratio-</span>
                        {getAspectRatio(field.imageSize)},
                        <span style={{ fontWeight: 600 }}> Res-</span>
                        300dpi
                      </Typography>
                    )}
                    {fieldErrors[`${group}-${field.name}`] && (
                      <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.75rem", mt: 0.5, display: "block", fontWeight: 500 }}>
                        {fieldErrors[`${group}-${field.name}`]}
                      </Typography>
                    )}
                    {(formFields[group]?.[field.name] instanceof File ||
                      (formFields[group]?.[field.name] &&
                        formFields[group]?.[field.name] !== field.defaultValue &&
                        formFields[group]?.[field.name].toString().startsWith('data:'))) && (
                        <ImageThumb
                          value={formFields[group]?.[field.name]}
                          label={field.label}
                          onView={(src, label) => setViewImage({ src, label })}
                        />
                      )}
                  </Box>
                ) : (
                  <Form.Control
                    type={field.type}
                    name={field.name}
                    autoComplete="on"
                    value={formFields[group]?.[field.name] || ""}
                    onChange={(e) => onChange(e, group, field.name, field.type)}
                    onFocus={() => {
                      onFocus(field.variableName || field.name, selectedTemplateConfig?.type);
                    }}
                    maxLength={field.maxLength ? field.maxLength : null}
                    placeholder={field.placeholder ? field.placeholder : ""}
                    isInvalid={
                      validated &&
                      (field.required === true || field.required === "true") &&
                      !formFields[group]?.[field.name]?.trim()
                    }
                    style={{
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '0.9rem',
                      backgroundColor: (field.variableName || field.name) === highlightFieldName ? '#fffbeb' : '#fff',
                      border: (field.variableName || field.name) === highlightFieldName ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                      boxShadow: (field.variableName || field.name) === highlightFieldName ? '0 0 0 3px rgba(245, 158, 11, 0.1)' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25, minHeight: '1.25rem' }}>
                  {validated && (field.required === true || field.required === "true") && (
                    !formFields[group]?.[field.name] ||
                    (field.type !== "file" && !formFields[group]?.[field.name]?.toString()?.trim()) ||
                    (field.type === "file" && formFields[group]?.[field.name] === field.defaultValue)
                  ) && (
                      <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem', display: 'block', margin: 0 }}>
                        {field.label} is required
                      </Form.Control.Feedback>
                    )}

                  {field.maxLength && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: (formFields[group]?.[field.name] || "").length >= field.maxLength ? '#ef4444' : '#94a3b8',
                        fontWeight: (formFields[group]?.[field.name] || "").length >= field.maxLength ? 700 : 400,
                        ml: 'auto',
                        fontSize: '0.7rem'
                      }}
                    >
                      {(formFields[group]?.[field.name] || "").length}/{field.maxLength}
                    </Typography>
                  )}
                </Box>
              </Form.Group>
            ))}
          </Box>
        </fieldset>
      ))}

      <Button
        variant="primary"
        type="submit"
        className="generate-button"
        style={{
          background: 'var(--primary-gradient)',
          border: 'none',
          padding: '5px 15px 7px',
          fontWeight: 700,
          borderRadius: '8px',
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          boxShadow: '0 4px 12px rgba(47, 83, 155, 0.2)',
          fontSize: '0.82rem'
        }}
      >
        <VisibilityIcon sx={{ fontSize: 16 }} />
        Preview
      </Button>

      {/* Large image preview (shared by all image fields) */}
      <Modal
        show={!!viewImage}
        onHide={() => setViewImage(null)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1e293b" }}>
            {viewImage?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#f1f5f9",
            padding: 12,
          }}
        >
          {viewImage && (
            <img
              src={viewImage.src}
              alt={viewImage.label || "Selected image"}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </Form>
  );

};
export default ReusableForm;